import { useEffect, useState } from "react";
import { GripVertical, Save, Loader2 } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "../../components/ui/sonner";
import { listarTodosServicos } from "../../lib/api";
import { reorderServicos, updateServico } from "../../lib/adminP1Api";
import { useAuth } from "../AuthContext";
import { Card, PageTitle } from "../ui";

const CATS = ["preventiva", "diagnostico", "cirurgia", "urgencia", "outros", "petshop"];
const CAT_LABEL = {
  preventiva: "Medicina Preventiva", diagnostico: "Diagnóstico", cirurgia: "Cirurgia",
  urgencia: "Urgências", outros: "Outros Cuidados", petshop: "PetShop",
};

function SortableRow({ s, canEdit, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.slug });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : 0, opacity: isDragging ? 0.7 : 1 };
  return (
    <tr ref={setNodeRef} style={style} data-testid={`servico-row-${s.slug}`} className="border-t border-[#1E5F6E]/10 bg-white">
      <td className="py-2 px-3 w-10">
        {canEdit && (
          <button {...attributes} {...listeners} data-testid={`drag-${s.slug}`} aria-label={`Reordenar ${s.nome}`} className="grid h-8 w-8 place-items-center rounded-lg text-[#5A5A5A] hover:bg-[#FAF7F2] cursor-grab">
            <GripVertical className="h-4 w-4" />
          </button>
        )}
      </td>
      <td className="py-2 px-3">
        <div className="font-semibold">{s.nome}</div>
        <div className="text-xs text-[#5A5A5A] line-clamp-1">{s.descricao}</div>
      </td>
      <td className="py-2 px-3 text-xs text-[#5A5A5A]">{CAT_LABEL[s.categoria]}</td>
      <td className="py-2 px-3 text-xs">{s.duracao_minutos} min</td>
      <td className="py-2 px-3 text-center">
        <label className="inline-flex items-center cursor-pointer" title="Disponível online">
          <input
            type="checkbox" disabled={!canEdit}
            checked={!!s.disponivel_marcacao_online}
            onChange={(e) => onToggle(s.slug, "disponivel_marcacao_online", e.target.checked)}
            data-testid={`online-${s.slug}`}
            className="h-4 w-4 accent-[#1E5F6E]"
          />
        </label>
      </td>
      <td className="py-2 px-3 text-center">
        <label className="inline-flex items-center cursor-pointer" title="Ativo">
          <input
            type="checkbox" disabled={!canEdit}
            checked={!!s.ativo}
            onChange={(e) => onToggle(s.slug, "ativo", e.target.checked)}
            data-testid={`ativo-${s.slug}`}
            className="h-4 w-4 accent-[#1E5F6E]"
          />
        </label>
      </td>
    </tr>
  );
}

export default function AdminServicosPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const carregar = async () => {
    setLoading(true);
    const r = await listarTodosServicos();
    setItems(r); setLoading(false);
  };
  useEffect(() => { carregar(); }, []);

  const groups = CATS.map((c) => ({ cat: c, items: items.filter((s) => s.categoria === c) }));

  const onDragEnd = (cat, ev) => {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    const list = items.filter((s) => s.categoria === cat);
    const oldIndex = list.findIndex((s) => s.slug === active.id);
    const newIndex = list.findIndex((s) => s.slug === over.id);
    const newList = arrayMove(list, oldIndex, newIndex);
    const others = items.filter((s) => s.categoria !== cat);
    setItems([...others, ...newList]);
    setDirty(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      // Reorder per category — backend sets ordem from 1..N
      for (const g of groups) {
        const ordem = items.filter((s) => s.categoria === g.cat).map((s) => s.slug);
        await reorderServicos(ordem);
      }
      toast.success("Ordem guardada.");
      setDirty(false);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao guardar.");
    } finally { setSaving(false); }
  };

  const onToggle = async (slug, field, value) => {
    try {
      await updateServico(slug, { [field]: value });
      toast.success("Atualizado.");
      setItems((arr) => arr.map((s) => s.slug === slug ? { ...s, [field]: value } : s));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar.");
    }
  };

  return (
    <div data-testid="admin-servicos">
      <div className="flex items-start justify-between gap-3 mb-7 flex-wrap">
        <PageTitle subtitle={canEdit ? "Gerir serviços, ordem e disponibilidade." : "Lista de serviços (apenas leitura)."}>Serviços</PageTitle>
        {canEdit && dirty && (
          <button onClick={saveOrder} disabled={saving} data-testid="btn-save-order" className="inline-flex items-center gap-2 rounded-full bg-[#1E5F6E] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#174E5B] disabled:opacity-40">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar ordem
          </button>
        )}
      </div>

      {loading && <p className="text-[#5A5A5A]">A carregar...</p>}

      {groups.map(({ cat, items: catItems }) => (
        <div key={cat} className="mb-7">
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-[#88A096] mb-3">{CAT_LABEL[cat]}</h3>
          <Card className="!p-0 overflow-hidden">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(ev) => onDragEnd(cat, ev)}>
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-[10px] uppercase tracking-wider text-[#5A5A5A]">
                  <tr>
                    <th className="text-left py-2 px-3 w-10"></th>
                    <th className="text-left py-2 px-3">Serviço</th>
                    <th className="text-left py-2 px-3">Categoria</th>
                    <th className="text-left py-2 px-3">Duração</th>
                    <th className="text-center py-2 px-3">Online</th>
                    <th className="text-center py-2 px-3">Ativo</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext items={catItems.map((s) => s.slug)} strategy={verticalListSortingStrategy}>
                    {catItems.map((s) => (
                      <SortableRow key={s.slug} s={s} canEdit={canEdit} onToggle={onToggle} />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          </Card>
        </div>
      ))}
    </div>
  );
}
